import {useContext, useState} from "react";
import {ServiceSurveysWithIdSchema, ServicesWithIdSchema, WorksitesWithIdSchema} from "@feature/types";
import useCountryAxiosInstance from "@hooks/useCountryAxiosInstance";
import AuthenticationService from "@services/AuthenticationService";
import { useIntl } from "react-intl";
import {NotificationsContext} from "@ui-components/Notifications";
import useAsyncError from "@hooks/useAsyncError";

export const useGenerateSurvey = (worksite: WorksitesWithIdSchema) => {

  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();
  const apiRequest = useCountryAxiosInstance();
  const throwError = useAsyncError();

  // state
  const [submitting, setSubmitting] = useState(false);

  // utilities
  const userData = AuthenticationService.getUserData();

  // handlers
  const upsert = async (data: {
    survey?: ServiceSurveysWithIdSchema,
    service: ServicesWithIdSchema,
    name: string,
  }) => {
    setSubmitting(true);
    try {
      let result: ServiceSurveysWithIdSchema;
      if (!!data.survey)
        // CASE A: existing survey --> update its name
        result = await apiRequest!.put(`/service_surveys/${data.survey.id}`, {
          ...data.survey,
          name: data.name,
        });
      else
        // CASE B: new survey --> create it
        result = await apiRequest!.post("/service_surveys", {
          name: data.name,
          service_id: data.service.id,
          id_source_surveys: [],
          worksite_id: worksite.id,
          complete: false,
          closed: false,
          blocked: false,
          r_perm: worksite.r_perm,
          w_perm: worksite.w_perm,
          id_creator: userData.id,
          service_version: data.service.active_version,
          specified_status: 'unspecified',
        });
      // return modified survey
      return result;
    } catch (error) {
      push({type: "error", title: intl.formatMessage({ id: "server_error" })});
      throwError(error);
      return null;
    } finally {
      setSubmitting(false);
    }
  }

  return {upsert, submitting, setSubmitting}
}
